import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Lock, Edit3, Sliders, Play, CheckCircle2, RotateCcw,
  GitCompare, BarChart3, AlertTriangle, AlertCircle, Loader2,
  ChevronRight, ArrowRightCircle, Sparkles, Zap, Pause, FileText,
  Star, Thermometer, Droplets, Wind, X, Archive, GitBranch,
  Activity, Target,
} from "lucide-react";
import api from "../lib/api";
import CasesSidebar from "./CasesSidebar";
import SankeyEnergyFlow from "./SankeyEnergyFlow";
import {
  ProductQualityTable, DutiesBarChart, StreamHMBTable,
  BranchStatusPanel, GroupedAdjustablePanel,
} from "./AutoSimEnhancements";
import DynamicSimulationTab from "./DynamicSimulationTab";
import OptimizationTab from "./OptimizationTab";

/**
 * Auto-Sim Workspace — multi-case simulation manager.
 *
 * Implements `Capexara Process Industry_Simulation_Engine_Definitive.md` Parts 1-2 in full:
 *   • Case tabs · Locked / Adjustable / Sensitivity panel
 *   • Run · Solver-On-Hold · Compare · Approve · Reset-to-DB
 *   • Results: Convergence banner + Worst-case envelope + Energy Sankey
 *     + Streams · Duties · Utilities · Quality · AI Assessment
 *   • AI Advisor with Apply Fix · Apply All Fixes & Re-Run ·
 *     [Go to A.1] · [Generate Revision Request]
 *   • Sensitivity sweep with dual-axis line chart and optimal point
 */
export default function AutoSimWorkspace({ projectId, onOpenStage }) {
  const [data, setData] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [solverHold, setSolverHold] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const [resultsTab, setResultsTab] = useState("ai");
  const [showSensitivity, setShowSensitivity] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showNewCase, setShowNewCase] = useState(false);
  const [staleEdits, setStaleEdits] = useState(0);
  const [lockedTip, setLockedTip] = useState("");
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Wave AS — Parameter validation dialog state
  const [validationDialog, setValidationDialog] = useState(null);
  // Wave AS — Duplicate-result alert state
  const [duplicateAlert, setDuplicateAlert] = useState(null);

  const reload = async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/projects/${projectId}/simulation/cases`);
      setData(r.data);
      if (!activeRun && r.data.cases.length > 0) {
        setActiveRun(r.data.cases[0].run_id);
      }
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  // Reset stale-edits counter when active case changes
  useEffect(() => { setStaleEdits(0); }, [activeRun]);

  const cases = data?.cases || [];
  const current = cases.find((c) => c.run_id === activeRun) || cases.find((c) => !c.archived);
  const defaults = data?.defaults;

  const handleCreate = async ({ name, baseRunId, description }) => {
    setErr("");
    try {
      const r = await api.post(`/projects/${projectId}/simulation/cases`, {
        name: name || `Case ${cases.length + 1}`,
        base_run_id: baseRunId,
        description: description || "",
      });
      await reload();
      setActiveRun(r.data.run_id);
      setShowNewCase(false);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const handleParamChange = async (cat, key, value) => {
    if (!current || cat === "locked_from_design_basis") return;
    setData((d) => {
      if (!d) return d;
      const next = JSON.parse(JSON.stringify(d));
      const c = next.cases.find((x) => x.run_id === current.run_id);
      if (c) {
        c.parameters[cat] = c.parameters[cat] || {};
        c.parameters[cat][key] = value;
        c.status = "draft";
      }
      return next;
    });
    setStaleEdits((n) => n + 1);
    if (solverHold) return; // accumulate edits
    try {
      await api.patch(`/projects/${projectId}/simulation/cases/${current.run_id}`,
        { parameter_patch: { [cat]: { [key]: value } } });
    } catch { /* ignore — autosave best-effort */ }
  };

  const handleRun = async (opts = {}) => {
    if (!current) return;
    setRunning(true); setErr(""); setValidationDialog(null); setDuplicateAlert(null);
    try {
      await api.patch(`/projects/${projectId}/simulation/cases/${current.run_id}`,
        { parameter_patch: current.parameters });
      const r = await api.post(`/projects/${projectId}/simulation/cases/${current.run_id}/run`);
      setData((d) => {
        if (!d) return d;
        const next = JSON.parse(JSON.stringify(d));
        const idx = next.cases.findIndex((c) => c.run_id === current.run_id);
        if (idx >= 0) next.cases[idx] = r.data;
        return next;
      });
      setStaleEdits(0);
      // Wave AS — surface duplicate warning if the engine flagged one
      if (r.data?.duplicate_warning) {
        setDuplicateAlert(r.data.duplicate_warning);
      }
    } catch (e) {
      // Wave AS — if validation failed, pop the dialog instead of a raw error
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "invalid_parameters") {
        setValidationDialog(detail.validation);
      } else {
        setErr(typeof detail === "string" ? detail : (detail?.message || e.message));
      }
    }
    finally { setRunning(false); }
  };

  // Wave AS — Apply all suggested fixes then re-run
  const handleApplyValidationFixes = async () => {
    if (!validationDialog || !current) return;
    const errors = validationDialog.errors || [];
    // Build a patch dict splitting adjustable vs sensitivity fields
    const sensFields = new Set([
      "ambient_temp_c", "cw_supply_temp_c", "cw_return_temp_c", "feed_rate_factor",
    ]);
    const patch = { adjustable: {}, sensitivity: {} };
    for (const e of errors) {
      const cat = sensFields.has(e.field) ? "sensitivity" : "adjustable";
      patch[cat][e.field] = e.suggested;
    }
    // Patch optimistically in local state
    setData((d) => {
      if (!d) return d;
      const next = JSON.parse(JSON.stringify(d));
      const c = next.cases.find((x) => x.run_id === current.run_id);
      if (c) {
        for (const cat of ["adjustable", "sensitivity"]) {
          c.parameters[cat] = { ...(c.parameters[cat] || {}), ...patch[cat] };
        }
      }
      return next;
    });
    // Persist + close dialog. User re-clicks Run themselves.
    try {
      await api.patch(`/projects/${projectId}/simulation/cases/${current.run_id}`,
        { parameter_patch: patch });
    } catch { /* ignore */ }
    setValidationDialog(null);
  };

  const handleApprove = async () => {
    if (!current) return;
    setErr("");
    try {
      await api.post(`/projects/${projectId}/simulation/cases/${current.run_id}/approve`);
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const handleResetToDB = async () => {
    if (!current) return;
    if (!window.confirm("Reset Adjustable + Sensitivity parameters of this case to the Design-Basis defaults? Results will be cleared.")) return;
    setErr("");
    try {
      await api.post(`/projects/${projectId}/simulation/cases/${current.run_id}/reset-to-db`);
      await reload();
      setStaleEdits(0);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const handleDeleteCase = async (runId, name) => {
    if (!runId) return;
    if (!window.confirm(`Archive case "${name}"? It will be hidden from the tabs strip but kept in history (you can restore later).`)) return;
    setErr("");
    try {
      await api.delete(`/projects/${projectId}/simulation/cases/${runId}`);
      if (activeRun === runId) setActiveRun(null);
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const handleUnarchiveCase = async (runId) => {
    setErr("");
    try {
      await api.post(`/projects/${projectId}/simulation/cases/${runId}/unarchive`);
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const handleTogglePin = async (runId) => {
    setErr("");
    try {
      await api.post(`/projects/${projectId}/simulation/cases/${runId}/pin`, {});
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const handleBulkArchive = async () => {
    const targets = cases.filter(
      (c) => !c.is_design_case && !c.archived
        && (c.status === "draft" || c.status === "diverged"),
    );
    if (targets.length === 0) {
      window.alert("No draft or diverged cases to archive.");
      return;
    }
    if (!window.confirm(`Archive ${targets.length} case(s) with status draft or diverged? They can be restored later.`)) return;
    setErr(""); setBulkArchiving(true);
    try {
      await api.post(`/projects/${projectId}/simulation/cases/bulk-archive`,
        { statuses: ["draft", "diverged"] });
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBulkArchiving(false); }
  };

  const applyFix = async (issue) => {
    if (!current || !issue.parameter_to_adjust) return;
    let value = issue.suggested_value;
    if (typeof value !== "number") {
      const cur = (current.parameters?.adjustable || {})[issue.parameter_to_adjust];
      if (typeof cur === "number") {
        value = cur + 0.2;
      } else { return; }
    }
    await handleParamChange("adjustable", issue.parameter_to_adjust, value);
    await handleRun();
  };

  const applyQualityFix = async (row) => {
    if (!current) return;
    const adj = current.parameters?.adjustable || {};
    const param = row.parameter?.toLowerCase() || "";
    // Map suggestion → concrete adjustable param bump
    let paramKey = null, delta = 0;
    if (param.includes("flash") || param.includes("purity") || param.includes("yield")) {
      // Raise reflux ratio by 0.2
      paramKey = Object.keys(adj).find((k) => /reflux/i.test(k)) || "reflux_ratio_multiplier";
      delta = 0.2;
    } else if (param.includes("freeze") || param.includes("cloud") || param.includes("pour")
               || param.includes("cetane")) {
      // Raise feed tray / side-draw tray by 2
      paramKey = Object.keys(adj).find((k) => /feed_tray|sidedraw.*_tray/i.test(k)) || "feed_tray";
      delta = -2;
    }
    if (!paramKey) {
      alert(`No adjustable parameter maps to "${row.parameter}". Manual adjustment required — see suggestion.`);
      return;
    }
    const cur = typeof adj[paramKey] === "number" ? adj[paramKey] : (paramKey.includes("reflux") ? 1.3 : 20);
    const next = cur + delta;
    await handleParamChange("adjustable", paramKey, next);
    await handleRun();
  };

  const applyAllFixes = async () => {
    if (!current?.ai_assessment?.issues) return;
    const fixable = current.ai_assessment.issues.filter(
      (i) => i.parameter_to_adjust && !i.requires_design_basis_change,
    );
    if (fixable.length === 0) return;
    // Patch parameters in one go
    const patch = { adjustable: {} };
    fixable.forEach((iss) => {
      let v = iss.suggested_value;
      if (typeof v !== "number") {
        const cur = (current.parameters?.adjustable || {})[iss.parameter_to_adjust];
        if (typeof cur === "number") v = cur + 0.2;
        else return;
      }
      patch.adjustable[iss.parameter_to_adjust] = v;
    });
    setRunning(true); setErr("");
    try {
      await api.patch(`/projects/${projectId}/simulation/cases/${current.run_id}`,
        { parameter_patch: patch });
      const r = await api.post(`/projects/${projectId}/simulation/cases/${current.run_id}/run`);
      setData((d) => {
        if (!d) return d;
        const next = JSON.parse(JSON.stringify(d));
        const idx = next.cases.findIndex((c) => c.run_id === current.run_id);
        if (idx >= 0) next.cases[idx] = r.data;
        return next;
      });
      setStaleEdits(0);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setRunning(false); }
  };

  const generateRevisionRequest = async () => {
    if (!current) return;
    try {
      const r = await api.post(`/projects/${projectId}/simulation/cases/${current.run_id}/revision-request`);
      // Trigger a markdown download
      const blob = new Blob([r.data.markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.data.filename || `revision_request_${current.run_id}.md`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  if (!data) {
    return (
      <div data-testid="autosim-loading" style={{ padding: 24, color: "var(--pf-text-muted)", fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
        Loading Auto-Sim cases…
      </div>
    );
  }

  return (
    <div data-testid="autosim-workspace" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {err && <div data-testid="autosim-error" className="pf-sev pf-sev-error" style={{ padding: "8px 12px", fontSize: 12 }}>{err}</div>}

      {/* Wave AS — Parameter validation error dialog */}
      {validationDialog && (
        <ValidationErrorDialog
          validation={validationDialog}
          onApplyAll={handleApplyValidationFixes}
          onClose={() => setValidationDialog(null)}
        />
      )}

      {/* Wave AS — Duplicate result alert */}
      {duplicateAlert && (
        <DuplicateResultAlert
          alert={duplicateAlert}
          onDismiss={() => setDuplicateAlert(null)}
        />
      )}

      {/* Locked field tooltip popover */}
      {lockedTip && (
        <div data-testid="autosim-locked-tip" style={{
          position: "fixed", top: 24, right: 24, zIndex: 80,
          padding: "10px 14px", background: "rgba(15,23,42,0.96)",
          color: "#cbd5e1", fontSize: 12, borderRadius: 4,
          border: "1px solid #475569", maxWidth: 320,
        }}>
          <div style={{ display: "flex", alignItems: "start", gap: 8 }}>
            <Lock size={14} style={{ marginTop: 2, color: "#94a3b8", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 4 }}>{lockedTip}</div>
              <button onClick={() => onOpenStage?.("A.1")} style={btnGhost} data-testid="autosim-locked-tip-goto">
                <ArrowRightCircle size={11} /> Open Stage A.1
              </button>
            </div>
            <button onClick={() => setLockedTip("")} style={{
              background: "transparent", color: "#94a3b8",
              border: 0, cursor: "pointer", padding: 0,
            }}><X size={13} /></button>
          </div>
        </div>
      )}

      {/* 2-column layout: cases sidebar + workspace canvas */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <CasesSidebar
          cases={cases}
          activeRun={activeRun}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
          onPick={(id) => setActiveRun(id)}
          onPin={handleTogglePin}
          onArchive={(id, name) => handleDeleteCase(id, name)}
          onUnarchive={handleUnarchiveCase}
          onNewCase={() => setShowNewCase(true)}
          onCompare={() => setShowCompare((v) => !v)}
        />

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Top toolbar — bulk-archive cleanup */}
          {cases.filter((c) => !c.is_design_case && !c.archived
            && (c.status === "draft" || c.status === "diverged")).length > 0 && (
            <div style={{
              display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8,
              padding: "4px 0",
            }}>
              <button
                type="button"
                data-testid="autosim-bulk-archive-btn"
                onClick={handleBulkArchive}
                disabled={bulkArchiving}
                style={{ ...btnGhost,
                  background: "rgba(234,179,8,0.10)", borderColor: "#eab308", color: "#fde68a",
                  opacity: bulkArchiving ? 0.6 : 1,
                }}
                title="Archive all draft and diverged cases (kept in history, restorable)"
              >{bulkArchiving ? <Loader2 className="pf-spin" size={11} /> : <Archive size={11} />} Cleanup draft/diverged</button>
            </div>
          )}

          {showNewCase && (
            <NewCaseDialog
              cases={cases}
              defaultBaseRunId={current?.run_id}
              onCreate={handleCreate}
              onClose={() => setShowNewCase(false)}
            />
          )}

          {!current ? (
            <div style={{ padding: 24, color: "var(--pf-text-muted)" }}>
              No cases yet. Create one to get started.
            </div>
          ) : (
            <>
              {/* Parameter panel */}
              <ParameterPanel
                projectId={projectId}
                parameters={current.parameters || defaults}
                onChange={handleParamChange}
                onLockedClick={(key) => setLockedTip(
                  `${key} is from Design Basis (A.1). To change it, go to Stage A.1 and create a revision.`
                )}
                solverHold={solverHold}
                staleEdits={staleEdits}
                onToggleSolver={() => setSolverHold((v) => !v)}
                onRun={handleRun}
                onApprove={handleApprove}
                onResetToDB={handleResetToDB}
                running={running}
                current={current}
                onShowSensitivity={() => setShowSensitivity(true)}
              />

              {showSensitivity && (
                <SensitivityDialog
                  projectId={projectId}
                  baseRunId={current.run_id}
                  onClose={() => setShowSensitivity(false)}
                  onCaseCreated={(runId) => { reload(); if (runId) setActiveRun(runId); }}
                />
              )}

              {showCompare && (
                <CompareTable projectId={projectId} onClose={() => setShowCompare(false)} />
              )}

              {/* Results */}
              {current.results && (
                <ResultsBlock
                  projectId={projectId}
                  current={current}
                  tab={resultsTab}
                  setTab={setResultsTab}
                  onApplyFix={applyFix}
                  onApplyQualityFix={applyQualityFix}
                  onApplyAllFixes={applyAllFixes}
                  onOpenStage={onOpenStage}
                  onGenerateRevisionRequest={generateRevisionRequest}
                  running={running}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const btnGhost = {
  display: "inline-flex", alignItems: "center", gap: 4,
  padding: "5px 10px", fontSize: 11.5, borderRadius: 3,
  background: "transparent", border: "1px solid var(--pf-border)",
  color: "var(--pf-text)", cursor: "pointer",
};

function StatusDot({ status }) {
  const COLORS = { draft: "#94a3b8", running: "#eab308", converged: "#22c55e", diverged: "#ef4444" };
  return <span style={{
    width: 7, height: 7, borderRadius: "50%",
    background: COLORS[status] || COLORS.draft,
    display: "inline-block",
  }} title={status} />;
}

// ────────── Parameter Panel ──────────
function ParameterPanel({
  projectId,
  parameters, onChange, onLockedClick, solverHold, staleEdits,
  onToggleSolver, onRun, onApprove, onResetToDB, running, current, onShowSensitivity,
}) {
  const locked = parameters?.locked_from_design_basis || {};
  const adjustable = parameters?.adjustable || {};
  const sensitivity = parameters?.sensitivity || {};

  return (
    <div data-testid="autosim-param-panel" style={{
      display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10,
      padding: 14, background: "var(--pf-surface)",
      border: "1px solid var(--pf-border)", borderRadius: 6,
    }}>
      <ParamColumn
        title="Locked (Design Basis)"
        Icon={Lock} color="#64748b"
        items={Object.entries(locked).map(([k, v]) => ({ key: k, value: v, locked: true }))}
        onChange={() => {}}
        onLockedClick={onLockedClick}
        cat="locked_from_design_basis"
      />
      <ParamColumn
        title="Adjustable (Operating)"
        Icon={Edit3} color="#3b82f6"
        items={Object.entries(adjustable).map(([k, v]) => ({ key: k, value: v }))}
        onChange={(k, v) => onChange("adjustable", k, v)}
        cat="adjustable"
      />
      <ParamColumn
        title="Sensitivity (Sweep)"
        Icon={Sliders} color="#8b5cf6"
        items={Object.entries(sensitivity).map(([k, v]) => ({ key: k, value: v }))}
        onChange={(k, v) => onChange("sensitivity", k, v)}
        cat="sensitivity"
      />

      {/* Wave AP — Grouped adjustable panel (auto-generated from equipment list) */}
      {projectId && (
        <div style={{ gridColumn: "1 / -1", marginTop: 4, paddingTop: 10,
                       borderTop: "1px solid var(--pf-border)" }}>
          <details data-testid="autosim-grouped-adjustable-wrapper" style={{ cursor: "pointer" }}>
            <summary style={{ color: "var(--pf-text-muted)", fontSize: 11,
                               display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <Edit3 size={11} />
              <span>EQUIPMENT-LEVEL PARAMETERS (auto-generated from flowsheet)</span>
            </summary>
            <GroupedAdjustablePanel
              projectId={projectId}
              currentAdjustable={adjustable}
              onChange={(k, v) => onChange("adjustable", k, v)}
            />
          </details>
        </div>
      )}

      {/* Footer */}
      <div style={{
        gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 10,
        marginTop: 4, paddingTop: 10, borderTop: "1px solid var(--pf-border)",
        flexWrap: "wrap",
      }}>
        <label data-testid="autosim-solver-hold" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--pf-text-muted)", cursor: "pointer" }}>
          <input type="checkbox" checked={solverHold} onChange={onToggleSolver} />
          <Pause size={12} /> Hold solver while editing
        </label>
        {solverHold && staleEdits > 0 && (
          <span data-testid="autosim-stale-edits" style={{
            fontSize: 11, padding: "3px 8px", borderRadius: 99,
            background: "rgba(234,179,8,0.15)", border: "1px solid #eab308", color: "#fde68a",
          }}>{staleEdits} parameter{staleEdits === 1 ? "" : "s"} changed since last run</span>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" data-testid="autosim-reset-db-btn" onClick={onResetToDB} style={btnGhost}>
          <RotateCcw size={12} /> Reset to DB
        </button>
        <button
          type="button"
          data-testid="autosim-sensitivity-btn"
          onClick={onShowSensitivity}
          style={btnGhost}
        ><BarChart3 size={12} /> Sensitivity sweep</button>
        <button
          type="button"
          data-testid="autosim-run-btn"
          onClick={onRun}
          disabled={running}
          style={{ ...btnGhost,
            background: "rgba(59,130,246,0.15)", borderColor: "#3b82f6", color: "#7dd3fc",
            opacity: running ? 0.6 : 1,
          }}
        >{running ? <Loader2 className="pf-spin" size={12} /> : <Play size={12} />} Run case</button>
        {current.status === "converged" && !current.is_design_case && (
          <button
            type="button"
            data-testid="autosim-approve-btn"
            onClick={onApprove}
            style={{ ...btnGhost,
              background: "rgba(34,197,94,0.15)", borderColor: "#22c55e", color: "#86efac",
            }}
          ><CheckCircle2 size={12} /> Mark as Design Case</button>
        )}
      </div>
    </div>
  );
}

function ParamColumn({ title, Icon, color, items, onChange, onLockedClick, cat }) {
  return (
    <div data-testid={`param-col-${cat}`} style={{
      padding: 10, background: "var(--pf-surface-2)",
      border: `1px solid ${color}40`, borderRadius: 4,
    }}>
      <div className="pf-overline" style={{ color, display: "flex", alignItems: "center", gap: 5, marginBottom: 8 }}>
        <Icon size={11} /> {title}
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--pf-text-muted)", fontStyle: "italic" }}>None</div>
      ) : items.map(({ key, value, locked }) => (
        <div key={key} style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 9.5, color: "var(--pf-text-muted)", marginBottom: 2, fontFamily: "'JetBrains Mono', monospace" }}>{key}</div>
          <input
            data-testid={`param-${cat}-${key}`}
            value={value ?? ""}
            disabled={!!locked}
            title={locked ? `${key} is from Design Basis (A.1). To change it, go to Stage A.1 and create a revision.` : undefined}
            onClick={() => locked && onLockedClick?.(key)}
            onChange={(e) => {
              const v = e.target.value;
              const num = parseFloat(v);
              onChange(key, isNaN(num) ? v : num);
            }}
            className="pf-input pf-mono"
            style={{
              width: "100%", padding: "4px 6px", fontSize: 11,
              background: "var(--pf-surface)",
              color: locked ? "var(--pf-text-muted)" : "var(--pf-text)",
              border: "1px solid var(--pf-border)", borderRadius: 3,
              cursor: locked ? "help" : "text",
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ────────── Results Block (with Convergence, Envelope, Sankey) ──────────
function ResultsBlock({ projectId, current, tab, setTab, onApplyFix, onApplyQualityFix, onApplyAllFixes,
                        onOpenStage, onGenerateRevisionRequest, running }) {
  const r = current.results || {};
  const a = current.ai_assessment || {};
  const conv = r.convergence || {};
  const TABS = [
    { id: "ai",       label: "AI Assessment",    icon: Sparkles, badge: a.issues?.length || null },
    { id: "branches", label: "Branches",         icon: GitBranch },
    { id: "energy",   label: "Energy Flow",      icon: Zap },
    { id: "streams",  label: "Streams",          icon: GitCompare },
    { id: "duties",   label: "Equipment Duties", icon: Zap },
    { id: "utility",  label: "Utilities",        icon: Droplets },
    { id: "quality",  label: "Product Quality",  icon: CheckCircle2 },
    { id: "dynamic",  label: "Dynamic",          icon: Activity },
    { id: "optimize", label: "Optimization",     icon: Target },
  ];
  return (
    <div data-testid="autosim-results" style={{
      background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 6, padding: 12,
    }}>
      {/* Convergence banner */}
      <ConvergenceBanner conv={conv} status={current.status} />

      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--pf-border)", marginBottom: 12, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`autosim-result-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            style={{
              padding: "7px 11px", fontSize: 11.5, background: "transparent",
              color: tab === t.id ? "var(--pf-spark)" : "var(--pf-text-muted)",
              border: 0, borderBottom: `2px solid ${tab === t.id ? "var(--pf-spark)" : "transparent"}`,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
            }}
          >
            <t.icon size={11} /> {t.label}
            {t.badge && <span style={{ fontSize: 9, padding: "1px 5px", background: "#ef4444", color: "#fff", borderRadius: 99 }}>{t.badge}</span>}
          </button>
        ))}
      </div>
      {tab === "ai" && (
        <AIAssessmentCard a={a}
          onApplyFix={onApplyFix}
          onApplyAllFixes={onApplyAllFixes}
          onOpenStage={onOpenStage}
          onGenerateRevisionRequest={onGenerateRevisionRequest}
          running={running} />
      )}
      {tab === "branches" && <BranchStatusPanel projectId={projectId} runId={current.run_id} />}
      {tab === "energy" && <EnergyFlowTab results={r} projectId={projectId} runId={current.run_id} />}
      {tab === "streams" && <StreamHMBTable streams={r.streams || []} />}
      {tab === "duties" && <DutiesBarChart duties={r.equipment_duties || []} />}
      {tab === "utility" && <KVTable obj={r.utility_summary || {}} />}
      {tab === "quality" && <ProductQualityTable projectId={projectId} runId={current.run_id} qualityRaw={r.product_quality || {}} onApplyFix={onApplyQualityFix} />}
      {tab === "dynamic" && <DynamicSimulationTab projectId={projectId} runId={current.run_id} />}
      {tab === "optimize" && <OptimizationTab projectId={projectId} runId={current.run_id} />}
    </div>
  );
}

function ConvergenceBanner({ conv, status }) {
  const mass = conv.mass_error_pct;
  const energy = conv.energy_error_pct;
  const iters = conv.iterations;
  const ok = status === "converged" && (mass === undefined || mass <= 0.5);
  const color = ok ? "#22c55e" : "#ef4444";
  return (
    <div data-testid="autosim-convergence-banner" style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "8px 12px", marginBottom: 10, borderRadius: 4,
      background: `${color}15`, border: `1px solid ${color}50`,
      fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5,
      flexWrap: "wrap",
    }}>
      <span style={{ color, fontWeight: 600 }}>
        {ok ? "✓ CONVERGED" : "✗ DIVERGED"}
      </span>
      <span style={{ color: "var(--pf-text-muted)" }}>
        Mass balance: <span style={{ color: "var(--pf-text)" }}>{fmt(mass, "%")}</span>
      </span>
      <span style={{ color: "var(--pf-text-muted)" }}>
        Energy balance: <span style={{ color: "var(--pf-text)" }}>{fmt(energy, "%")}</span>
      </span>
      {iters !== undefined && (
        <span style={{ color: "var(--pf-text-muted)" }}>
          Iterations: <span style={{ color: "var(--pf-text)" }}>{iters}</span>
        </span>
      )}
    </div>
  );
}

function fmt(v, unit) {
  if (v === undefined || v === null) return "—";
  return `${(+v).toFixed(2)}${unit || ""}`;
}

// ────────── Energy Flow tab — Worst-case envelope tiles + Sankey ──────────
function EnergyFlowTab({ results, projectId, runId }) {
  const totals = results.totals || {};
  const utility = results.utility_summary || {};
  // LNG terminals report seawater under `cw_medium="seawater"`. The default
  // condenser-type detection only worked for refining (air vs water-cooled),
  // so detect LNG explicitly to relabel the cooling tile.
  const isLng = String(utility.cw_medium || "").toLowerCase() === "seawater"
                  || results._engine === "lng_lightweight_v1";
  const airCooled = !isLng && (
                     String(utility.condenser_type || "").startsWith("air")
                     || (utility.cw_supply_m3h === 0 && (totals.air_cooler_duty_kw || 0) > 0));
  const envelope = {
    heating_duty_kW: totals.reboiler_duty_kw || totals.heating_duty_kw
                       || totals.vaporizer_duty_kw || 0,
    cooling_duty_kW: (totals.condenser_duty_kw || totals.cooling_duty_kw || 0)
                      + (totals.air_cooler_duty_kw || 0),
    electrical_kW:   totals.electrical_kw || totals.pump_power_kw || 0,
    MP_steam_kgph:   utility.mp_steam_kgph
                      || (totals.heating_duty_kw && !isLng ? totals.heating_duty_kw * 1.65 : 0),
    cooling_water_m3ph: utility.cw_supply_m3h || 0,
    air_flow_kgs:       utility.air_cooler_airflow_kgs || 0,
  };
  return (
    <div data-testid="autosim-energy-tab">
      {/* AS.2 — surface that the baseline was synthesised from feed rate */}
      {results.baseline_synthesised && (
        <div data-testid="autosim-synthesised-banner" style={{
          padding: "8px 12px", marginBottom: 12, borderRadius: 4,
          background: "#3b82f615", border: "1px solid #3b82f655",
          borderLeft: "3px solid #3b82f6", color: "var(--pf-text)",
          fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
        }}>
          <b style={{ color: "#93c5fd" }}>ⓘ BASELINE SYNTHESISED</b>
          {" — "}No equipment_duties were found in the PDO for this project, so
          duties were computed from first-principles using the design-basis feed
          rate + your adjustable parameters. Values are first-order approximations
          suitable for engineering-judgment iteration; run Stage A simulation to
          replace this baseline with a rigorous solve.
        </div>
      )}
      {/* AS.3 — surface when totals were clamped due to extreme params */}
      {results.totals_capped && (
        <div data-testid="autosim-totals-capped-banner" style={{
          padding: "8px 12px", marginBottom: 12, borderRadius: 4,
          background: "#f59e0b15", border: "1px solid #f59e0b",
          borderLeft: "3px solid #f59e0b", color: "var(--pf-text)",
          fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
        }}>
          <b style={{ color: "#fcd34d" }}>⚠ TOTALS CLAMPED AT 500 MW</b>
          {" — "}One or more energy totals exceeded 500 MW and were clamped for
          display. This usually indicates physically extreme parameters (check
          reflux ratio, column pressure, heater outlet temp) or an AI-dumped PDO
          with corrupt units. Reduce the parameters or run Stage A simulation
          to replace the duties with a rigorous solve.
        </div>
      )}
      <div className="pf-overline" style={{ marginBottom: 8 }}>WORST-CASE ENVELOPE (FOR SIZING)</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 10, marginBottom: 22 }}>
        <Tile testid="env-heating"  Icon={Thermometer} label={isLng ? "Vaporization" : "Heating"}        value={`${envelope.heating_duty_kW.toFixed(0)} kW`} />
        <Tile testid="env-cooling"  Icon={Droplets}    label="Cooling"        value={`${envelope.cooling_duty_kW.toFixed(0)} kW`} />
        <Tile testid="env-electric" Icon={Zap}         label="Electrical"     value={`${envelope.electrical_kW.toFixed(0)} kW`} />
        {isLng ? (
          <Tile testid="env-bog"   Icon={Wind}        label="BOG Generation"
                value={`${(+(totals.bog_generation_kgh || 0)).toFixed(0)} kg/h`} />
        ) : (
          <Tile testid="env-mpsteam"  Icon={Wind}        label="MP Steam"
                value={`${(+envelope.MP_steam_kgph).toFixed(0)} kg/h`} />
        )}
        {isLng ? (
          <Tile testid="env-seawater" Icon={Droplets}   label="Seawater Flow"
                value={`${(+envelope.cooling_water_m3ph).toFixed(0)} m³/h`} />
        ) : airCooled ? (
          <Tile testid="env-airflow" Icon={Wind}      label="Air Cooler Flow"
                value={`${(+envelope.air_flow_kgs).toFixed(1)} kg/s`} />
        ) : (
          <Tile testid="env-cw"     Icon={Droplets}   label="Cooling Water"
                value={`${(+envelope.cooling_water_m3ph).toFixed(1)} m³/h`} />
        )}
      </div>

      <div className="pf-overline" style={{ marginBottom: 8 }}>ENERGY FLOW · SANKEY</div>
      <SankeyEnergyFlow results={results} projectId={projectId} runId={runId} />
    </div>
  );
}

function Tile({ Icon, label, value, testid }) {
  return (
    <div data-testid={testid} style={{
      padding: 12, background: "var(--pf-surface-2)",
      border: "1px solid var(--pf-border)", borderRadius: 4,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, color: "var(--pf-text-muted)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>
        <Icon size={11} /> {label}
      </div>
      <div style={{ fontSize: 18, fontFamily: "'JetBrains Mono', monospace", color: "var(--pf-text)" }}>
        {value}
      </div>
    </div>
  );
}

// ────────── AI Assessment ──────────
function AIAssessmentCard({ a, onApplyFix, onApplyAllFixes, onOpenStage,
                             onGenerateRevisionRequest, running }) {
  if (!a || !a.issues) return <div style={{ color: "var(--pf-text-muted)" }}>No assessment yet — run the case first.</div>;
  const SEV = {
    CRITICAL: { color: "#ef4444", Icon: AlertCircle },
    HIGH:     { color: "#f97316", Icon: AlertTriangle },
    MEDIUM:   { color: "#eab308", Icon: AlertTriangle },
    LOW:      { color: "#94a3b8", Icon: ChevronRight },
  };
  const fixable = a.issues.filter((i) => i.parameter_to_adjust && !i.requires_design_basis_change);
  return (
    <div data-testid="autosim-ai-assessment">
      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 10,
        padding: 10, background: "var(--pf-surface-2)", borderRadius: 4,
      }}>
        <div style={{ fontSize: 32, fontFamily: "'JetBrains Mono', monospace", color: a.overall_score >= 70 ? "#22c55e" : a.overall_score >= 50 ? "#eab308" : "#ef4444" }}>
          {a.overall_score}
        </div>
        <div style={{ flex: 1 }}>
          <div className="pf-overline" style={{ color: "var(--pf-text-muted)" }}>OVERALL SCORE</div>
          <div data-testid="autosim-ai-recommendation" style={{ fontSize: 13, color: "var(--pf-text)", marginTop: 2 }}>
            {a.recommendation}
          </div>
        </div>
        {fixable.length >= 2 && (
          <button
            type="button"
            data-testid="autosim-apply-all-fixes-btn"
            onClick={onApplyAllFixes}
            disabled={running}
            style={{ ...btnGhost,
              background: "rgba(59,130,246,0.15)", borderColor: "#3b82f6", color: "#7dd3fc",
              opacity: running ? 0.6 : 1,
            }}
          >
            {running ? <Loader2 className="pf-spin" size={12} /> : <Sparkles size={12} />}
            Apply all {fixable.length} fixes & re-run
          </button>
        )}
      </div>

      {a.requires_design_basis_change && (
        <div data-testid="autosim-revise-banner" style={{
          padding: "10px 14px", marginBottom: 10, borderRadius: 4,
          background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", color: "#fca5a5",
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠ Design Basis revision needed</div>
          <div style={{ fontSize: 12 }}>Some issues cannot be fixed by parameter adjustment. Open Stage A.1 and revise, or download a formal Revision Request.</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              data-testid="autosim-goto-a1"
              onClick={() => onOpenStage?.("A.1")}
              style={{
                padding: "5px 10px", fontSize: 11,
                background: "rgba(239,68,68,0.20)", border: "1px solid #ef4444", color: "#fca5a5",
                borderRadius: 3, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
              }}
            ><ArrowRightCircle size={12} /> Go to A.1 to Revise Design Basis</button>
            <button
              type="button"
              data-testid="autosim-revision-request-btn"
              onClick={onGenerateRevisionRequest}
              style={{
                padding: "5px 10px", fontSize: 11,
                background: "transparent", border: "1px solid #ef4444", color: "#fca5a5",
                borderRadius: 3, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
              }}
            ><FileText size={12} /> Generate Revision Request</button>
          </div>
        </div>
      )}

      {a.issues.length === 0 && (
        <div data-testid="autosim-no-issues" style={{ padding: 14, color: "#86efac", fontSize: 12, background: "rgba(34,197,94,0.10)", border: "1px solid #22c55e", borderRadius: 4 }}>
          ✓ No issues detected. This case meets all design criteria.
        </div>
      )}

      {a.issues.map((iss, idx) => {
        const sev = SEV[iss.severity] || SEV.LOW;
        return (
          <div key={idx} data-testid={`autosim-issue-${idx}`} style={{
            padding: 10, marginBottom: 8, borderRadius: 4,
            background: `${sev.color}10`, border: `1px solid ${sev.color}40`,
          }}>
            <div style={{ display: "flex", alignItems: "start", gap: 8 }}>
              <sev.Icon size={14} color={sev.color} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: sev.color, fontWeight: 600 }}>
                  {iss.severity} · {iss.category}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--pf-text)", margin: "4px 0" }}>
                  {iss.message}
                </div>
                {iss.suggestion && (
                  <div style={{ fontSize: 11.5, color: "var(--pf-text-muted)", lineHeight: 1.5 }}>
                    <b>Suggestion:</b> {iss.suggestion}
                  </div>
                )}
                {iss.parameter_to_adjust && !iss.requires_design_basis_change && (
                  <button
                    type="button"
                    data-testid={`autosim-apply-fix-${idx}`}
                    onClick={() => onApplyFix(iss)}
                    style={{
                      marginTop: 6, padding: "4px 9px", fontSize: 10.5,
                      background: "rgba(59,130,246,0.15)", border: "1px solid #3b82f6", color: "#7dd3fc",
                      borderRadius: 3, cursor: "pointer",
                    }}
                  >Apply fix → {iss.parameter_to_adjust}{iss.suggested_value ? ` = ${iss.suggested_value}` : ""}</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────── Sensitivity dialog with dual-axis line chart ──────────
function SensitivityDialog({ projectId, baseRunId, onClose, onCaseCreated }) {
  const [variable, setVariable] = useState("reflux_ratio_multiplier");
  const [start, setStart] = useState(1.1);
  const [end, setEnd] = useState(2.0);
  const [step, setStep] = useState(0.1);
  const [track1, setTrack1] = useState("reboiler_duty_kw");
  const [track2, setTrack2] = useState("");
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingCase, setSavingCase] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setLoading(true); setErr(""); setSeries(null);
    try {
      const tracks = [track1, track2].filter(Boolean);
      const r = await api.post(`/projects/${projectId}/simulation/cases/sensitivity`, {
        base_run_id: baseRunId, variable,
        start: +start, end: +end, step: +step,
        track: tracks,
      });
      setSeries(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="autosim-sensitivity-dialog" style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 70,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        width: "min(880px, 95vw)", maxHeight: "90vh", overflow: "auto",
        background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
        borderRadius: 6, padding: 18,
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Sensitivity Analysis</h3>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} data-testid="autosim-sensitivity-close" style={btnGhost}>Close</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 10 }}>
          <Field testid="sens-var" label="Variable to change" v={variable} onChange={setVariable} />
          <Field testid="sens-start" label="Start" v={start} onChange={setStart} />
          <Field testid="sens-end" label="End" v={end} onChange={setEnd} />
          <Field testid="sens-step" label="Step" v={step} onChange={setStep} />
          <Field testid="sens-track1" label="Track 1 (left Y-axis)" v={track1} onChange={setTrack1} />
          <Field testid="sens-track2" label="Track 2 (right Y-axis, optional)" v={track2} onChange={setTrack2} />
        </div>
        <button data-testid="autosim-sensitivity-run" onClick={run} disabled={loading}
                style={{ ...btnGhost, background: "var(--pf-spark)", color: "#0a0e14", borderColor: "var(--pf-spark)" }}>
          {loading ? <Loader2 className="pf-spin" size={12} /> : <Play size={12} />} Run sweep
        </button>
        {err && <div className="pf-sev pf-sev-error" style={{ marginTop: 10, padding: 8, fontSize: 11 }}>{err}</div>}
        {series && (
          <div data-testid="autosim-sensitivity-results" style={{ marginTop: 14 }}>
            <DualAxisLineChart series={series} variable={variable} track1={track1} track2={track2} />
            {series.optimal && (
              <div data-testid="autosim-sensitivity-optimal" style={{
                marginTop: 8, padding: 8,
                background: "rgba(34,197,94,0.10)", border: "1px solid #22c55e",
                borderRadius: 3, fontSize: 11.5, color: "#86efac",
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
              }}>
                <Star size={11} />
                <span>
                  Optimal: {series.variable} = {fmtNum(series.optimal[series.variable])}
                  {track1 && ` · ${track1} = ${fmtNum(series.optimal[track1])}`}
                  {track2 && series.optimal[track2] !== undefined && ` · ${track2} = ${fmtNum(series.optimal[track2])}`}
                </span>
                <div style={{ flex: 1 }} />
                <button
                  data-testid="autosim-sensitivity-save-case"
                  disabled={savingCase}
                  onClick={async () => {
                    setSavingCase(true); setErr("");
                    try {
                      const optVal = series.optimal[series.variable];
                      const caseName = `Optimal ${series.variable}=${fmtNum(optVal)}`;
                      const r = await api.post(`/projects/${projectId}/simulation/cases`, {
                        name: caseName,
                        description: `Derived from sensitivity sweep on ${series.variable}. Optimal point: ${series.variable}=${fmtNum(optVal)}${track1 ? `, ${track1}=${fmtNum(series.optimal[track1])}` : ""}.`,
                        base_run_id: baseRunId,
                      });
                      // Patch the adjustable parameter to the optimum
                      if (r.data?.run_id) {
                        try {
                          await api.patch(`/projects/${projectId}/simulation/cases/${r.data.run_id}`, {
                            parameters: { adjustable: { [series.variable]: optVal } },
                          });
                        } catch { /* non-fatal */ }
                      }
                      if (onCaseCreated) onCaseCreated(r.data?.run_id);
                      onClose();
                    } catch (e) {
                      setErr(e?.response?.data?.detail || e.message);
                    } finally { setSavingCase(false); }
                  }}
                  style={{ padding: "4px 10px", background: "rgba(59,130,246,0.20)",
                           border: "1px solid #3b82f6", color: "#7dd3fc", borderRadius: 3,
                           cursor: savingCase ? "wait" : "pointer", fontSize: 11,
                           display: "inline-flex", alignItems: "center", gap: 5 }}
                >
                  {savingCase ? <Loader2 className="pf-spin" size={11} /> : <Plus size={11} />}
                  Save as New Case
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtNum(v) {
  if (v === undefined || v === null) return "—";
  if (typeof v === "number") return v.toFixed(2);
  return String(v);
}

function DualAxisLineChart({ series, variable, track1, track2 }) {
  const data = series.series || [];
  if (data.length === 0) return <div style={{ color: "var(--pf-text-muted)", fontSize: 12 }}>No points.</div>;

  const W = 800, H = 320;
  const PAD = { top: 30, right: 70, bottom: 50, left: 70 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const xs = data.map((p) => +p[variable]);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);

  const y1Vals = data.map((p) => +p[track1]).filter((v) => Number.isFinite(v));
  const y1Min = y1Vals.length ? Math.min(...y1Vals) : 0;
  const y1Max = y1Vals.length ? Math.max(...y1Vals) : 1;
  const y1Range = y1Max - y1Min || 1;

  const y2Vals = track2 ? data.map((p) => +p[track2]).filter((v) => Number.isFinite(v)) : [];
  const y2Min = y2Vals.length ? Math.min(...y2Vals) : 0;
  const y2Max = y2Vals.length ? Math.max(...y2Vals) : 1;
  const y2Range = y2Max - y2Min || 1;

  const sx = (v) => PAD.left + ((v - xMin) / (xMax - xMin || 1)) * innerW;
  const sy1 = (v) => PAD.top + (1 - (v - y1Min) / y1Range) * innerH;
  const sy2 = (v) => PAD.top + (1 - (v - y2Min) / y2Range) * innerH;

  const path1 = data
    .filter((p) => Number.isFinite(+p[track1]))
    .map((p, i) => `${i === 0 ? "M" : "L"} ${sx(+p[variable])} ${sy1(+p[track1])}`)
    .join(" ");
  const path2 = track2 ? data
    .filter((p) => Number.isFinite(+p[track2]))
    .map((p, i) => `${i === 0 ? "M" : "L"} ${sx(+p[variable])} ${sy2(+p[track2])}`)
    .join(" ") : "";

  const xTicks = 5;
  const tickVals = Array.from({ length: xTicks }, (_, i) => xMin + (i * (xMax - xMin)) / (xTicks - 1));
  const yTicks = 5;
  const y1TickVals = Array.from({ length: yTicks }, (_, i) => y1Min + (i * y1Range) / (yTicks - 1));
  const y2TickVals = track2 ? Array.from({ length: yTicks }, (_, i) => y2Min + (i * y2Range) / (yTicks - 1)) : [];

  const optimal = series.optimal;
  const optX = optimal ? sx(+optimal[variable]) : null;
  const optY1 = optimal && Number.isFinite(+optimal[track1]) ? sy1(+optimal[track1]) : null;

  return (
    <div data-testid="autosim-sensitivity-chart" style={{
      padding: 8, background: "var(--pf-surface)",
      border: "1px solid var(--pf-border)", borderRadius: 4,
    }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", fontFamily: "'JetBrains Mono', monospace" }}>
        {/* Grid */}
        {y1TickVals.map((v, i) => (
          <line key={`g${i}`} x1={PAD.left} y1={sy1(v)} x2={W - PAD.right} y2={sy1(v)}
                stroke="var(--pf-border)" strokeDasharray="2,3" strokeOpacity="0.4" />
        ))}
        {/* Y axis left ticks */}
        {y1TickVals.map((v, i) => (
          <g key={`y1-${i}`}>
            <line x1={PAD.left - 4} y1={sy1(v)} x2={PAD.left} y2={sy1(v)} stroke="#3b82f6" />
            <text x={PAD.left - 8} y={sy1(v) + 3} textAnchor="end" fontSize="9" fill="#7dd3fc">
              {v.toFixed(1)}
            </text>
          </g>
        ))}
        {/* Y axis right ticks */}
        {track2 && y2TickVals.map((v, i) => (
          <g key={`y2-${i}`}>
            <line x1={W - PAD.right} y1={sy2(v)} x2={W - PAD.right + 4} y2={sy2(v)} stroke="#a855f7" />
            <text x={W - PAD.right + 8} y={sy2(v) + 3} textAnchor="start" fontSize="9" fill="#d8b4fe">
              {v.toFixed(1)}
            </text>
          </g>
        ))}
        {/* X axis ticks */}
        {tickVals.map((v, i) => (
          <g key={`x-${i}`}>
            <line x1={sx(v)} y1={H - PAD.bottom} x2={sx(v)} y2={H - PAD.bottom + 4} stroke="var(--pf-text-muted)" />
            <text x={sx(v)} y={H - PAD.bottom + 16} textAnchor="middle" fontSize="9" fill="var(--pf-text-muted)">
              {v.toFixed(2)}
            </text>
          </g>
        ))}
        {/* Axes */}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={H - PAD.bottom} stroke="#3b82f6" />
        {track2 && <line x1={W - PAD.right} y1={PAD.top} x2={W - PAD.right} y2={H - PAD.bottom} stroke="#a855f7" />}
        <line x1={PAD.left} y1={H - PAD.bottom} x2={W - PAD.right} y2={H - PAD.bottom} stroke="var(--pf-text-muted)" />
        {/* Series lines */}
        <path d={path1} stroke="#3b82f6" strokeWidth="2" fill="none" data-testid="chart-line-track1" />
        {track2 && <path d={path2} stroke="#a855f7" strokeWidth="2" fill="none" data-testid="chart-line-track2" />}
        {/* Data points */}
        {data.filter((p) => Number.isFinite(+p[track1])).map((p, i) => (
          <circle key={`p1-${i}`} cx={sx(+p[variable])} cy={sy1(+p[track1])} r="3" fill="#3b82f6" />
        ))}
        {track2 && data.filter((p) => Number.isFinite(+p[track2])).map((p, i) => (
          <circle key={`p2-${i}`} cx={sx(+p[variable])} cy={sy2(+p[track2])} r="3" fill="#a855f7" />
        ))}
        {/* Optimal star */}
        {optX !== null && optY1 !== null && (
          <g data-testid="chart-optimal-star">
            <circle cx={optX} cy={optY1} r="8" fill="none" stroke="#22c55e" strokeWidth="2" />
            <text x={optX} y={optY1 - 12} textAnchor="middle" fontSize="11" fill="#86efac">★ optimal</text>
          </g>
        )}
        {/* Axis labels */}
        <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--pf-text)">{variable}</text>
        <text transform={`translate(16, ${(H - PAD.bottom + PAD.top) / 2}) rotate(-90)`} textAnchor="middle" fontSize="11" fill="#7dd3fc">{track1}</text>
        {track2 && <text transform={`translate(${W - 16}, ${(H - PAD.bottom + PAD.top) / 2}) rotate(-90)`} textAnchor="middle" fontSize="11" fill="#d8b4fe">{track2}</text>}
      </svg>
    </div>
  );
}

function Field({ label, v, onChange, testid }) {
  return (
    <label style={{ display: "block", fontSize: 11, color: "var(--pf-text-muted)" }}>
      <div style={{ marginBottom: 3 }}>{label}</div>
      <input
        data-testid={testid}
        value={v} onChange={(e) => onChange(e.target.value)}
        className="pf-input pf-mono"
        style={{ width: "100%", padding: "5px 8px", fontSize: 11.5,
                 background: "var(--pf-surface)", color: "var(--pf-text)",
                 border: "1px solid var(--pf-border)", borderRadius: 3 }} />
    </label>
  );
}

// ────────── New Case Dialog ──────────
function NewCaseDialog({ cases, defaultBaseRunId, onCreate, onClose }) {
  const [name, setName] = useState("");
  const [baseRunId, setBaseRunId] = useState(defaultBaseRunId || "");
  const [description, setDescription] = useState("");

  return (
    <div data-testid="autosim-new-case-dialog" style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 70,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        width: "min(520px, 95vw)",
        background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
        borderRadius: 6, padding: 18,
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>New Simulation Case</h3>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} data-testid="autosim-new-case-close" style={btnGhost}>
            <X size={12} /> Close
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <label style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>
            <div style={{ marginBottom: 3 }}>Case Name</div>
            <input
              data-testid="new-case-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Summer Case - Max Ambient"
              className="pf-input pf-mono"
              style={{ width: "100%", padding: "6px 10px", fontSize: 12,
                       background: "var(--pf-surface)", color: "var(--pf-text)",
                       border: "1px solid var(--pf-border)", borderRadius: 3 }}
            />
          </label>
          <label style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>
            <div style={{ marginBottom: 3 }}>Base on (copy parameters from)</div>
            <select
              data-testid="new-case-base-on"
              value={baseRunId}
              onChange={(e) => setBaseRunId(e.target.value)}
              className="pf-mono"
              style={{ width: "100%", padding: "6px 10px", fontSize: 12,
                       background: "var(--pf-surface)", color: "var(--pf-text)",
                       border: "1px solid var(--pf-border)", borderRadius: 3 }}
            >
              <option value="">— start from Design-Basis defaults —</option>
              {cases.map((c) => (
                <option key={c.run_id} value={c.run_id}>
                  {c.name} {c.is_design_case ? "★" : ""}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>
            <div style={{ marginBottom: 3 }}>Description (optional)</div>
            <textarea
              data-testid="new-case-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="pf-input pf-mono"
              placeholder="Notes on what makes this case different…"
              style={{ width: "100%", padding: "6px 10px", fontSize: 12,
                       background: "var(--pf-surface)", color: "var(--pf-text)",
                       border: "1px solid var(--pf-border)", borderRadius: 3, resize: "vertical" }}
            />
          </label>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button onClick={onClose} style={btnGhost} data-testid="new-case-cancel">Cancel</button>
          <button
            data-testid="new-case-create"
            onClick={() => onCreate({ name, baseRunId: baseRunId || undefined, description })}
            disabled={!name.trim()}
            style={{ ...btnGhost,
              background: "rgba(34,197,94,0.15)", borderColor: "#22c55e", color: "#86efac",
              opacity: name.trim() ? 1 : 0.5,
            }}
          ><Plus size={12} /> Create case</button>
        </div>
      </div>
    </div>
  );
}

// ────────── Compare table ──────────
function CompareTable({ projectId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/projects/${projectId}/simulation/cases/compare`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [projectId]);
  if (err) return <div data-testid="autosim-compare-err" className="pf-sev pf-sev-error">{err}</div>;
  if (!data) return <div data-testid="autosim-compare-loading" style={{ padding: 12, color: "var(--pf-text-muted)" }}>Loading comparison…</div>;
  return (
    <div data-testid="autosim-compare-table" style={{ padding: 12, background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", borderRadius: 4 }}>
      <div style={{ display: "flex", marginBottom: 8 }}>
        <span className="pf-overline">CASE COMPARISON</span>
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={btnGhost}>Close</button>
      </div>
      {data.cases.length < 2 ? (
        <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>Need at least 2 completed cases to compare.</div>
      ) : (
        <table className="pf-mono" style={{ fontSize: 11, width: "100%" }}>
          <thead>
            <tr>
              <th style={{ padding: "5px 8px", textAlign: "left", color: "var(--pf-text-muted)", borderBottom: "1px solid var(--pf-border)" }}>Metric</th>
              {data.cases.map((c) => (
                <th key={c.run_id} style={{ padding: "5px 8px", textAlign: "left", color: c.is_design_case ? "#86efac" : "var(--pf-text)", borderBottom: "1px solid var(--pf-border)" }}>
                  {c.is_design_case ? "★ " : ""}{c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, idx) => (
              <tr key={idx} style={{ background: row.section === "results" && idx > 0 && data.rows[idx - 1].section === "parameters" ? "var(--pf-surface)" : undefined }}>
                <td style={{ padding: "4px 8px", color: row.section === "parameters" ? "#7dd3fc" : "#fbbf24" }}>{row.label}</td>
                {row.values.map((v, j) => (
                  <td key={j} style={{ padding: "4px 8px", fontWeight: v.best ? 700 : 400, color: v.best ? "#22c55e" : "var(--pf-text)" }}>
                    {v.best && "↓ "}{typeof v.v === "object" ? JSON.stringify(v.v) : String(v.v ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SimpleTable({ rows, testid }) {
  if (!rows || rows.length === 0) return <div style={{ color: "var(--pf-text-muted)", fontSize: 12 }}>No rows.</div>;
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="pf-table pf-mono" data-testid={testid || "autosim-streams-table"} style={{ fontSize: 11 }}>
        <thead><tr>{cols.map((c) => <th key={c} style={{ padding: "5px 8px", textAlign: "left", borderBottom: "1px solid var(--pf-border)", color: "var(--pf-text-muted)" }}>{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i}>{cols.map((c) => <td key={c} style={{ padding: "4px 8px", borderBottom: "1px solid var(--pf-border)" }}>{typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "")}</td>)}</tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function KVTable({ obj }) {
  const entries = Object.entries(obj || {});
  if (entries.length === 0) return <div style={{ color: "var(--pf-text-muted)", fontSize: 12 }}>No data.</div>;
  return (
    <table className="pf-table pf-mono" style={{ fontSize: 11 }}>
      <tbody>{entries.map(([k, v]) => (
        <tr key={k}>
          <td style={{ padding: "5px 10px", color: "var(--pf-text-muted)" }}>{k}</td>
          <td style={{ padding: "5px 10px", color: "var(--pf-text)" }}>{typeof v === "object" ? JSON.stringify(v) : String(v ?? "")}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}


/* ─────────────────────────────────────────────────────────────────────
 * Wave AS — Parameter validation error dialog
 *
 * Blocks the run and lists every field that failed validation, each with
 * a "[Set to suggested]" one-click fix, plus a "Fix All to Suggested" CTA.
 * ────────────────────────────────────────────────────────────────── */
function ValidationErrorDialog({ validation, onApplyAll, onClose }) {
  const errors = validation?.errors || [];
  const warnings = validation?.warnings || [];
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  return (
    <div data-testid="autosim-validation-dialog"
         style={{
           position: "fixed", inset: 0, background: "rgba(15,23,42,0.72)",
           zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center",
         }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--pf-surface)", border: "1px solid #ef4444",
        borderLeft: "4px solid #ef4444", borderRadius: 4, padding: 0,
        maxWidth: 780, width: "90%", maxHeight: "85vh", overflow: "auto",
        ...mono, fontSize: 12,
      }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--pf-border)",
                       display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ color: "#fca5a5", fontSize: 14, fontWeight: 700, letterSpacing: 0.5 }}>
              ✗ CANNOT RUN SIMULATION — INVALID PARAMETERS
            </div>
            <div style={{ color: "var(--pf-text-muted)", fontSize: 11, marginTop: 3 }}>
              {errors.length} error{errors.length === 1 ? "" : "s"} detected.
              Fix these to proceed.
            </div>
          </div>
          <button onClick={onClose} data-testid="autosim-validation-close"
                  style={{ background: "transparent", border: 0, color: "var(--pf-text-muted)",
                            cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          {errors.map((e, i) => (
            <div key={i} data-testid={`autosim-validation-err-${e.field}`}
                 style={{
                   background: "#ef444415", border: "1px solid #ef444455",
                   borderLeft: "3px solid #ef4444", borderRadius: 3,
                   padding: "10px 12px",
                 }}>
              <div style={{ color: "#fca5a5", fontWeight: 700, marginBottom: 4 }}>
                ✗ {e.field} = <span style={{ color: "var(--pf-text)" }}>{String(e.value)}</span>
              </div>
              <div style={{ color: "var(--pf-text)", marginBottom: 6, lineHeight: 1.45 }}>
                {e.message}
              </div>
              <div style={{ color: "var(--pf-text-muted)", fontSize: 11 }}>
                Valid range: <b style={{ color: "var(--pf-text)" }}>{e.valid_range}</b>
                {e.suggested !== undefined && (
                  <span style={{ marginLeft: 10 }}>
                    · Suggested: <b style={{ color: "#86efac" }}>{e.suggested}</b>
                  </span>
                )}
              </div>
            </div>
          ))}
          {warnings.length > 0 && (
            <div style={{ marginTop: 8, paddingTop: 10,
                          borderTop: "1px dashed var(--pf-border)" }}>
              <div style={{ color: "#fcd34d", fontWeight: 700, marginBottom: 6, fontSize: 11 }}>
                ⚠ {warnings.length} WARNING{warnings.length === 1 ? "" : "S"} (non-blocking)
              </div>
              {warnings.map((w, i) => (
                <div key={i} style={{ color: "var(--pf-text-muted)", fontSize: 11, marginBottom: 4 }}>
                  <b style={{ color: "#fcd34d" }}>{w.field}</b>: {w.message}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--pf-border)",
                       display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose}
                  data-testid="autosim-validation-cancel"
                  style={{
                    background: "transparent", border: "1px solid var(--pf-border)",
                    color: "var(--pf-text-muted)", padding: "6px 14px",
                    borderRadius: 3, cursor: "pointer", ...mono, fontSize: 11,
                  }}>Cancel</button>
          <button type="button" onClick={onApplyAll}
                  data-testid="autosim-validation-fix-all"
                  style={{
                    background: "#22c55e", border: 0, color: "#052e16",
                    padding: "6px 14px", borderRadius: 3, cursor: "pointer",
                    fontWeight: 700, ...mono, fontSize: 11,
                  }}>FIX ALL TO SUGGESTED VALUES</button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
 * Wave AS — Duplicate-result alert
 *
 * Shown when the new run produced numerically identical results to an
 * existing case despite different parameters. This is a bug-canary —
 * if the user ever sees it, the engine skipped their parameter change.
 * ────────────────────────────────────────────────────────────────── */
function DuplicateResultAlert({ alert, onDismiss }) {
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  return (
    <div data-testid="autosim-duplicate-alert"
         style={{
           background: "#f59e0b15", border: "1px solid #f59e0b",
           borderLeft: "4px solid #f59e0b", borderRadius: 4,
           padding: "10px 14px", ...mono, fontSize: 12,
           display: "flex", alignItems: "start", gap: 10,
         }}>
      <div style={{ color: "#fcd34d", fontSize: 16, fontWeight: 700 }}>⚠</div>
      <div style={{ flex: 1 }}>
        <div style={{ color: "#fcd34d", fontWeight: 700, marginBottom: 4 }}>
          DUPLICATE RESULT DETECTED
        </div>
        <div style={{ color: "var(--pf-text)", lineHeight: 1.5 }}>
          {alert.message}
        </div>
        <div style={{ color: "var(--pf-text-muted)", marginTop: 6, fontSize: 11 }}>
          Identical to case: <b style={{ color: "var(--pf-text)" }}>{alert.duplicate_of_name}</b>
          {" · "}Tolerance: <b>{alert.tolerance_pct}%</b>
        </div>
      </div>
      <button onClick={onDismiss} data-testid="autosim-duplicate-dismiss"
              style={{ background: "transparent", border: 0,
                        color: "var(--pf-text-muted)",
                        cursor: "pointer", fontSize: 16 }}>×</button>
    </div>
  );
}
